import React, { useState, useEffect, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import BdsLoadingFullScreen from '../../../shared/components/v3/BdsLoadingFullScreen/BdsLoadingFullScreen';
import Footer from '../../../shared/components/v3/Footer/Footer';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { LOGIN_ROUTE } from '../../../shared/constants/auth';
import { RECAPTCHA_SITE_KEY } from '../../../shared/constants/constants';
import { CLICK_ACTION, CREATE_ACCOUNT, CREATE_PASSWORD } from '../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../shared/constants/toast';
import { SessionContext } from '../../../shared/context/auth/authContext';
import Users from '../../../shared/services/api/users/users';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import CreatePassword from '../sections/CreatePassword/CreatePassword';
import VerificationCode from '../sections/VerificationCode/VerificationCode';

const CreateAccount = () => {
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [verificationFailed, setVerificationFailed] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [loadingVerification, setLoadingVerification] = useState(false);

  const { sessionLogin, errorCode, resetError } = useContext(SessionContext);
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { email?: string; verificationCode?: string } | null;
  const email = state?.email || '';
  const verificationCode = state?.verificationCode || '';

  const { t } = useTranslation();

  useEffect(() => {
    const submitCode = async (code: string) => {
      setLoadingVerification(true);
      try {
        const response = await Users.verifyEmail(email, code);
        setVerificationToken(response.token);
        setStep(1);
      } finally {
        setLoadingVerification(false);
      }
    };

    if (!email) {
      navigate(LOGIN_ROUTE, { replace: true });
    }
    if (verificationCode) {
      submitCode(verificationCode);
    }
  }, [email, navigate, verificationCode]);

  useEffect(() => {
    if (errorCode) {
      setOpenToast(true);
      resetError('');
    }
  }, [errorCode, resetError]);

  const handleSignIn = async (userPassword: string) => {
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_PASSWORD);
    window.grecaptcha.ready(async () => {
      setLoading(true);
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
      await sessionLogin(email, userPassword, recaptchaToken);
      setLoading(false);
    });
  };

  const handleVerificationCode = async (code: string) => {
    try {
      setLoading(true);
      setVerificationFailed(false);
      const response = await Users.verifyEmail(email, code);
      setVerificationToken(response.token);
      setStep(1);
    } catch {
      setOpenToast(true);
      setVerificationFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (userPassword: string) => {
    setLoading(true);
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_ACCOUNT);
    try {
      await Users.signUp(email, userPassword, verificationToken);
      handleSignIn(userPassword);
    } catch {
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStep = (newStep: number) => {
    if (newStep === -1) {
      return navigate(LOGIN_ROUTE);
    }
    setStep(newStep);
  };

  if (loadingVerification) {
    return (
      <div className="auth-container" data-experience="new">
        <BdsLoadingFullScreen show={true} text={''} />
      </div>
    );
  }

  return (
    <>
      <div className="auth-container" data-experience="new">
        {step === 0 && (
          <VerificationCode
            codeType="create-account"
            email={email}
            loading={loading}
            hasError={verificationFailed}
            handleNext={handleVerificationCode}
            handleGoBack={handleChangeStep}
          />
        )}
        {step === 1 && (
          <CreatePassword
            onSubmit={handleCreateAccount}
            email={email}
            loading={loading}
            handleGoBack={handleChangeStep}
          />
        )}
        <Toast
          title={t(GENERIC_ERROR_TOAST.title)}
          description={t(GENERIC_ERROR_TOAST.description)}
          icon={GENERIC_ERROR_TOAST.icon}
          open={openToast}
          button={true}
          handleClose={() => setOpenToast(false)}
        />
      </div>
      <Footer login={true} />
    </>
  );
};

export default CreateAccount;
